import { useState, useEffect } from "react";
import { authAxios } from "../../utils/auth";
import {
  Mail, ChevronDown, ChevronRight, BarChart3, MessageSquare,
  MousePointerClick, ExternalLink
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid
} from "recharts";

const GOLD = "#C5A55A";

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1a1a1e] border border-white/10 rounded-lg px-3 py-2 text-xs shadow-xl">
      <p className="text-white/60 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>{p.name}: {p.value}%</p>
      ))}
    </div>
  );
}

export default function EmailerTab({ range }) {
  const [sequences, setSequences] = useState([]);
  const [clicks, setClicks] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSeq, setExpandedSeq] = useState(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      authAxios.get("/analytics/command/emailer/sequences"),
      authAxios.get(`/analytics/command/emailer/clicks?range=${range}`),
      authAxios.get("/analytics/command/emailer/engagement"),
    ]).then(([seqRes, clickRes, engRes]) => {
      setSequences(seqRes.data.sequences || []);
      setClicks(clickRes.data);
      setEngagement(engRes.data);
      if (seqRes.data.sequences?.length) setExpandedSeq(seqRes.data.sequences[0].id);
    }).catch(console.error).finally(() => setLoading(false));
  }, [range]);

  if (loading) return <div className="flex items-center justify-center h-64 text-white/20">Loading eMailer data...</div>;

  const activeSeq = sequences.find(s => s.id === expandedSeq);
  const chartData = activeSeq?.steps?.map(s => ({
    name: `Step ${s.step}`,
    open_rate: s.open_rate,
    click_rate: s.click_rate,
  })) || [];

  return (
    <div className="space-y-6">
      {/* ── Click Tracking Summary ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Total Clicks", value: clicks?.total_clicks || 0, icon: MousePointerClick },
          { label: "/read Clicks", value: clicks?.read_clicks || 0, icon: ExternalLink },
          { label: "/news Clicks", value: clicks?.news_clicks || 0, icon: ExternalLink },
          { label: "Comments", value: engagement?.total || 0, icon: MessageSquare },
        ].map(m => (
          <div key={m.label} className="rounded-xl border border-white/[0.06] bg-[#111113] p-4" data-testid={`emailer-metric-${m.label.replace(/\s/g,'-').toLowerCase()}`}>
            <div className="flex items-center gap-2 text-white/40 text-xs mb-1">
              <m.icon className="w-3.5 h-3.5" /><span>{m.label}</span>
            </div>
            <p className="text-2xl font-bold text-white">{m.value}</p>
          </div>
        ))}
      </div>

      {/* ── Sequences ── */}
      <section className="rounded-xl border border-white/[0.06] bg-[#111113] p-5" data-testid="emailer-sequences">
        <h2 className="text-sm font-semibold text-white/50 uppercase tracking-wider mb-4 flex items-center gap-2">
          <Mail className="w-4 h-4" /> Email Sequences
        </h2>
        <div className="space-y-2">
          {sequences.map(seq => {
            const isExpanded = expandedSeq === seq.id;
            return (
              <div key={seq.id} className="border border-white/[0.04] rounded-lg overflow-hidden" data-testid={`sequence-${seq.id}`}>
                <button
                  onClick={() => setExpandedSeq(isExpanded ? null : seq.id)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/[0.02] transition-colors text-left"
                >
                  <div className="flex items-center gap-3">
                    {isExpanded ? <ChevronDown className="w-4 h-4 text-white/30" /> : <ChevronRight className="w-4 h-4 text-white/30" />}
                    <span className="text-sm text-white/80 font-medium">{seq.name}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${seq.active ? "bg-emerald-500/15 text-emerald-400" : "bg-white/5 text-white/30"}`}>
                      {seq.active ? "Active" : "Inactive"}
                    </span>
                  </div>
                  <span className="text-xs text-white/30">{seq.steps.length} steps</span>
                </button>
                {isExpanded && (
                  <div className="px-4 pb-4">
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-white/30 border-b border-white/[0.06]">
                            <th className="text-left py-2 px-2 font-medium">#</th>
                            <th className="text-left py-2 px-2 font-medium">Subject</th>
                            <th className="text-right py-2 px-2 font-medium">Sent</th>
                            <th className="text-right py-2 px-2 font-medium">Delivered</th>
                            <th className="text-right py-2 px-2 font-medium">Open%</th>
                            <th className="text-right py-2 px-2 font-medium">Click%</th>
                            <th className="text-right py-2 px-2 font-medium">Bounce%</th>
                          </tr>
                        </thead>
                        <tbody>
                          {seq.steps.map(step => (
                            <tr key={step.step} className="border-b border-white/[0.03] hover:bg-white/[0.02]" data-testid={`step-row-${seq.id}-${step.step}`}>
                              <td className="py-2 px-2 text-white/30 font-mono">{step.step}</td>
                              <td className="py-2 px-2 text-white/70 max-w-[250px] truncate">{step.subject}</td>
                              <td className="py-2 px-2 text-right text-white/50 font-mono">{step.sent}</td>
                              <td className="py-2 px-2 text-right text-white/50 font-mono">{step.delivery_rate}%</td>
                              <td className="py-2 px-2 text-right font-mono">
                                <span className={step.open_rate > 30 ? "text-emerald-400" : step.open_rate > 15 ? "text-amber-400" : "text-red-400"}>
                                  {step.open_rate}%
                                </span>
                              </td>
                              <td className="py-2 px-2 text-right font-mono">
                                <span className={step.click_rate > 5 ? "text-emerald-400" : step.click_rate > 2 ? "text-amber-400" : "text-white/40"}>
                                  {step.click_rate}%
                                </span>
                              </td>
                              <td className="py-2 px-2 text-right font-mono">
                                <span className={step.bounce_rate > 5 ? "text-red-400" : "text-white/40"}>
                                  {step.bounce_rate}%
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {/* Drop-off curve */}
                    {seq.steps.length > 1 && (
                      <div className="mt-4 pt-4 border-t border-white/[0.04]">
                        <p className="text-xs text-white/30 mb-2">Open & Click Rate Drop-off</p>
                        <ResponsiveContainer width="100%" height={160}>
                          <LineChart data={seq.steps.map(s => ({ name: `Step ${s.step}`, open_rate: s.open_rate, click_rate: s.click_rate }))}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                            <XAxis dataKey="name" tick={{ fill: "#555", fontSize: 10 }} />
                            <YAxis tick={{ fill: "#555", fontSize: 10 }} />
                            <Tooltip content={<ChartTooltip />} />
                            <Line type="monotone" dataKey="open_rate" stroke={GOLD} strokeWidth={2} dot name="Open Rate" />
                            <Line type="monotone" dataKey="click_rate" stroke="#22c55e" strokeWidth={2} dot name="Click Rate" />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {!sequences.length && (
            <div className="text-center text-white/20 py-8 text-sm">No sequences found</div>
          )}
        </div>
      </section>

      {/* ── Top Clicked URLs ── */}
      {clicks?.top_urls?.length > 0 && (
        <section className="rounded-xl border border-white/[0.06] bg-[#111113] p-5" data-testid="emailer-top-urls">
          <h2 className="text-sm font-semibold text-white/50 uppercase tracking-wider mb-4 flex items-center gap-2">
            <MousePointerClick className="w-4 h-4" /> Top Clicked URLs
          </h2>
          <div className="space-y-2">
            {clicks.top_urls.map((u, i) => (
              <div key={i} className="flex items-center gap-3 text-xs py-1">
                <span className="text-white/30 font-mono w-5">{i + 1}</span>
                <span className="text-white/60 flex-1 truncate font-mono">{u.url}</span>
                <span className="text-white/80 font-mono font-bold">{u.count}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Comment Engagement ── */}
      {engagement && (engagement.total > 0 || engagement.by_email?.length > 0) && (
        <section className="rounded-xl border border-white/[0.06] bg-[#111113] p-5" data-testid="emailer-engagement">
          <h2 className="text-sm font-semibold text-white/50 uppercase tracking-wider mb-4 flex items-center gap-2">
            <MessageSquare className="w-4 h-4" /> Comment Engagement
          </h2>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="text-center"><p className="text-lg font-bold text-white">{engagement.total}</p><p className="text-[10px] text-white/30">Total</p></div>
            <div className="text-center"><p className="text-lg font-bold text-emerald-400">{engagement.approved}</p><p className="text-[10px] text-white/30">Approved</p></div>
            <div className="text-center"><p className="text-lg font-bold text-amber-400">{engagement.pending}</p><p className="text-[10px] text-white/30">Pending</p></div>
          </div>
          {engagement.by_email?.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs text-white/30">By Email Page</p>
              {engagement.by_email.map((e, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <span className="text-white/60 flex-1 truncate">{e.name || e.slug}</span>
                  <span className="text-white/80 font-mono">{e.total} ({e.approved} approved)</span>
                </div>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
